import { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  type ContentAuditRow,
  type DetectionProvider,
  getContentAudit,
  getDetectionProviders,
  scanContentAudit,
  patchContentAudit,
} from "../lib/api";
import { Button } from "../components/Form";
import type { WorkspaceContext } from "./ClientWorkspacePage";

export default function ContentAuditTab() {
  const { client } = useOutletContext<WorkspaceContext>();
  const [rows, setRows] = useState<ContentAuditRow[]>([]);
  const [hasCrawl, setHasCrawl] = useState(true);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // chunk3z-fix379: shown when pages fell back to the built-in estimate.
  const [warning, setWarning] = useState<string | null>(null);
  const [providers, setProviders] = useState<DetectionProvider[]>([]);
  const [provider, setProvider] = useState<string>("");
  // Live scan progress for the one-page-at-a-time loop.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(
    null
  );
  // Set when the user clicks Stop — the scan loop checks this and bails.
  const stopRef = useRef(false);
  // Page ids queued for the current scan but not yet completed — rendered faded
  // until each one finishes (then it returns to full color).
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    getDetectionProviders()
      .then((data) => {
        setProviders(data.providers);
        // chunk3z-fix379: prefer ZeroGPT ONLY when it's actually ready (a key is
        // configured). Previously we selected it regardless of status, so the
        // dropdown said "ZeroGPT" while every page was silently scored by the
        // free heuristic. Fall back to the first ready provider, else heuristic.
        const zerogpt = data.providers.find(
          (p) => p.id === "zerogpt" && p.status === "ready"
        );
        const firstReady = data.providers.find((p) => p.status === "ready");
        setProvider(zerogpt?.id ?? firstReady?.id ?? "heuristic");
      })
      .catch(() => setProviders([]));
  }, []);

  async function load() {
    setLoading(true);
    try {
      const data = await getContentAudit(client.id);
      setRows(data.rows);
      setHasCrawl(data.hasCrawl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load audit.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client.id]);

  function stopScan() {
    stopRef.current = true;
  }

  // Scan one page at a time so we can show real "N/total scanned" progress,
  // stream each score into the table as it completes, and support Stop. While
  // scanning, the target rows are faded (pendingIds); each returns to full
  // color the moment it finishes.
  async function handleScan(pageIds?: number[]) {
    const targetIds =
      pageIds && pageIds.length > 0 ? pageIds : rows.map((r) => r.id);
    if (targetIds.length === 0) return;

    stopRef.current = false;
    setScanning(true);
    setError(null);
    setInfo(null);
    setProgress({ done: 0, total: targetIds.length });
    setPendingIds(new Set(targetIds));

    let scored = 0;
    let failed = 0;
    // chunk3z-fix379: how many pages silently used the free heuristic instead
    // of the selected detector, and why.
    let fellBack = 0;
    let fallbackReason: string | null = null;
    let stopped = false;
    try {
      for (let i = 0; i < targetIds.length; i++) {
        if (stopRef.current) {
          stopped = true;
          break;
        }
        const id = targetIds[i]!;
        try {
          const result = await scanContentAudit(client.id, {
            pageIds: [id],
            provider: provider || undefined,
          });
          if (result.rows && result.rows.length > 0) {
            const byId = new Map(result.rows.map((r) => [r.id, r]));
            setRows((prev) => prev.map((r) => byId.get(r.id) ?? r));
          }
          scored += result.scored;
          failed += result.failed;
          // chunk3z-fix379: capture silent heuristic fallbacks so we can warn.
          if (result.fellBack && result.fellBack > 0) {
            fellBack += result.fellBack;
            fallbackReason = fallbackReason ?? result.fallbackReason ?? null;
          }
        } catch {
          failed += 1;
        }
        // This page is done — bring it back to full color.
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
        setProgress({ done: i + 1, total: targetIds.length });
      }
      setInfo(
        stopped
          ? `Scan stopped — ${scored} scored before stopping.`
          : `Scan complete — ${scored} scored${
              failed > 0 ? `, ${failed} could not be scored` : ""
            }.`
      );
      // chunk3z-fix379: if any page silently fell back to the free heuristic,
      // say so loudly — otherwise "est." scores look like real detector output.
      if (fellBack > 0) {
        setWarning(
          `${fellBack} page${fellBack === 1 ? "" : "s"} were scored with the built-in estimate, not ${
            provider === "zerogpt" ? "ZeroGPT" : provider
          }. ${fallbackReason ?? ""} Add a valid API key in Settings, then rescan.`.trim()
        );
      } else {
        setWarning(null);
      }
    } finally {
      setScanning(false);
      setProgress(null);
      setPendingIds(new Set());
      stopRef.current = false;
    }
  }

  async function toggleUpdated(row: ContentAuditRow) {
    const next = !row.isUpdated;
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, isUpdated: next } : r))
    );
    try {
      await patchContentAudit(client.id, row.id, { isUpdated: next });
    } catch {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isUpdated: !next } : r))
      );
    }
  }

  async function dismissNew(row: ContentAuditRow) {
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, isNew: false } : r))
    );
    try {
      await patchContentAudit(client.id, row.id, { isNew: false });
    } catch {
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isNew: true } : r))
      );
    }
  }

  const scoredCount = useMemo(
    () => rows.filter((r) => r.currentAiScore != null).length,
    [rows]
  );
  const updatedCount = useMemo(
    () => rows.filter((r) => r.isUpdated).length,
    [rows]
  );
  const newCount = useMemo(() => rows.filter((r) => r.isNew).length, [rows]);

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="bg-bg-card border border-border rounded-lg p-5 shadow-card flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-bg-subtle border border-border flex items-center justify-center text-text-muted shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          </div>
          <div>
            <h1 className="text-text font-semibold text-xl">Content Audit</h1>
            <p className="text-text-muted text-sm mt-0.5">
              Scan existing pages for an AI-detection score, track which you've
              rewritten, and rescan to measure the improvement.
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 shrink-0 min-w-[260px]">
          <div className="flex items-center gap-2">
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="bg-bg-input border border-border rounded px-2 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent max-w-[200px]"
              title="Which AI-detection service to score with"
            >
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.status === "no_key" ? " — no API key" : ""}
                </option>
              ))}
            </select>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleScan()}
              disabled={scanning || !hasCrawl}
            >
              {scanning ? "Scanning..." : "Scan all"}
            </Button>
            {scanning && (
              <Button variant="secondary" size="sm" onClick={stopScan}>
                Stop
              </Button>
            )}
          </div>

          {progress && (
            <div className="w-full">
              <div className="flex justify-between text-[11px] text-text-muted mb-1">
                <span>
                  {progress.done}/{progress.total} scanned
                </span>
                <span>
                  {Math.round((progress.done / progress.total) * 100)}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-bg-subtle rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all duration-200"
                  style={{
                    width: `${Math.round(
                      (progress.done / progress.total) * 100
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {!hasCrawl && (
        <div className="bg-bg-card border border-border rounded-lg p-8 text-center shadow-card">
          <p className="text-text-muted text-sm">
            No crawl data yet. Crawl the site on the Crawl tab first, then come
            back to scan AI scores.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-danger-bg border border-danger-border text-danger rounded-lg px-4 py-2.5 text-sm">
          {error}
        </div>
      )}
      {info && (
        <div className="bg-accent-subtle border border-accent/30 text-accent rounded-lg px-4 py-2.5 text-sm">
          {info}
        </div>
      )}

      {/* chunk3z-fix379: loud warning when the selected detector wasn't actually
          used and pages were scored with the free built-in estimate instead. */}
      {warning && (
        <div className="bg-warning/10 border border-warning/40 text-warning rounded-lg px-4 py-2.5 text-sm">
          ⚠ {warning}
        </div>
      )}

      {hasCrawl && (
        <div className="bg-bg-card border border-border rounded-lg shadow-card overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle">
            <div className="flex items-center gap-3 text-sm">
              <span className="text-text font-semibold">
                {rows.length} pages
              </span>
              <span className="text-text-subtle">·</span>
              <span className="text-text-muted">{scoredCount} scored</span>
              <span className="text-text-subtle">·</span>
              <span className="text-text-muted">{updatedCount} marked updated</span>
              {newCount > 0 && (
                <>
                  <span className="text-text-subtle">·</span>
                  <span className="text-accent font-medium">
                    {newCount} new since first crawl
                  </span>
                </>
              )}
            </div>
            <span className="text-text-subtle text-xs">
              Lower score = more human
            </span>
          </div>

          {loading ? (
            <div className="px-4 py-8 text-center text-text-muted text-sm">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="px-4 py-8 text-center text-text-muted text-sm">
              No service pages found to audit.
            </div>
          ) : (
            <table className="w-full text-sm table-fixed">
              <colgroup>
                <col style={{ width: "34%" }} />
                <col style={{ width: "130px" }} />
                <col style={{ width: "120px" }} />
                <col style={{ width: "140px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "80px" }} />
              </colgroup>
              <thead className="bg-bg-subtle border-b border-border-subtle text-text-muted text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5">Page</th>
                  <th className="text-center px-4 py-2.5">Words</th>
                  <th className="text-left px-4 py-2.5">AI Score</th>
                  <th className="text-center px-4 py-2.5">Change</th>
                  <th className="text-center px-4 py-2.5">Updated</th>
                  <th className="text-right px-4 py-2.5">Rescan</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <AuditRowView
                    key={r.id}
                    row={r}
                    displayTitle={cleanTitle(
                      r.title,
                      client.businessName,
                      client.businessAddress
                    )}
                    scanning={scanning}
                    pending={pendingIds.has(r.id)}
                    onToggleUpdated={() => toggleUpdated(r)}
                    onRescan={() => handleScan([r.id])}
                    onDismissNew={() => dismissNew(r)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function AuditRowView({
  row,
  displayTitle,
  scanning,
  pending,
  onToggleUpdated,
  onRescan,
  onDismissNew,
}: {
  row: ContentAuditRow;
  displayTitle: string;
  scanning: boolean;
  pending: boolean;
  onToggleUpdated: () => void;
  onRescan: () => void;
  onDismissNew: () => void;
}) {
  return (
    <tr
      className={`border-b border-border-subtle last:border-b-0 hover:bg-bg-hover/40 align-top transition-opacity duration-300 ${
        pending ? "opacity-40" : "opacity-100"
      }`}
    >
      <td className="px-4 py-2.5">
        <div className="flex items-start gap-2">
          <div className="min-w-0">
            <div className="text-text text-sm font-medium truncate" title={row.title ?? row.path}>
              {displayTitle || row.path}
            </div>
            <a
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-text-subtle hover:text-accent hover:underline font-mono text-[11px] break-all block mt-0.5"
              title={row.url}
            >
              {row.path}
            </a>
          </div>
          {row.isNew && (
            <button
              type="button"
              onClick={onDismissNew}
              className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-accent bg-accent-subtle border border-accent/30 rounded px-1.5 py-0.5 hover:bg-accent/15 mt-0.5"
              title="Added after the original crawl — click to dismiss"
            >
              New ✕
            </button>
          )}
        </div>
        {row.lastError && (
          <div className="text-danger text-xs mt-0.5">{row.lastError}</div>
        )}
      </td>
      <td className="px-4 py-2.5 text-center text-accent text-xs">
        {row.wordCount != null ? row.wordCount.toLocaleString() : "—"}
      </td>
      <td className="px-4 py-2.5">
        {row.currentAiScore != null ? (
          <div className="leading-tight">
            <div className="flex items-center gap-1.5">
              <ScoreBadge score={row.currentAiScore} />
              {row.scoreSource && (
                <span className="text-text-subtle text-[10px]">
                  {row.scoreSource === "heuristic" ? "est." : row.scoreSource}
                </span>
              )}
            </div>
            {row.bestAiScore != null &&
              row.worstAiScore != null &&
              row.bestAiScore !== row.worstAiScore && (
                <div className="text-text-subtle text-[10px] mt-1">
                  range {row.bestAiScore}–{row.worstAiScore}%
                </div>
              )}
          </div>
        ) : (
          <span className="text-text-subtle text-xs">not scored</span>
        )}
      </td>
      <td className="px-4 py-2.5 text-center">
        <ChangeIndicator
          current={row.currentAiScore}
          previous={row.previousAiScore}
        />
      </td>
      <td className="px-4 py-2.5 text-center">
        <input
          type="checkbox"
          checked={row.isUpdated}
          onChange={onToggleUpdated}
          className="accent-success"
          title="Mark as rewritten/updated"
        />
      </td>
      <td className="px-4 py-2.5 text-right">
        <button
          type="button"
          onClick={onRescan}
          disabled={scanning}
          className="text-accent hover:underline text-xs disabled:opacity-50"
        >
          Rescan
        </button>
      </td>
    </tr>
  );
}

// fix235: lower is better (more human). Green 0-30, yellow 31-40, red 41-100.
function ScoreBadge({ score }: { score: number }) {
  const tone =
    score <= 30
      ? "bg-success/15 text-success border-success/30"
      : score <= 40
        ? "bg-warning-bg text-warning border-warning-border"
        : "bg-danger-bg text-danger border-danger-border";
  return (
    <span
      className={`inline-block text-sm font-semibold px-2 py-0.5 rounded border ${tone}`}
    >
      {score}%
    </span>
  );
}

function ChangeIndicator({
  current,
  previous,
}: {
  current: number | null;
  previous: number | null;
}) {
  // No prior score to compare against.
  if (current == null || previous == null) {
    return <span className="text-text-subtle text-xl font-bold">—</span>;
  }
  const delta = current - previous; // negative = improved (lower AI = better)

  if (delta === 0) {
    return (
      <div className="flex flex-col items-center leading-tight">
        <span className="text-text text-xl font-bold">—</span>
        <span className="text-text-subtle text-[11px] mt-0.5">
          was {previous}%
        </span>
      </div>
    );
  }

  const improved = delta < 0; // score went DOWN = more human = good (green)
  return (
    <div className="flex flex-col items-center leading-tight">
      <span
        className={`text-xl font-bold ${
          improved ? "text-success" : "text-danger"
        }`}
      >
        {improved ? "−" : "+"}
        {Math.abs(delta)}
      </span>
      <span className="text-text-subtle text-[11px] mt-0.5">
        was {previous}%
      </span>
    </div>
  );
}

/**
 * Clean a page <title> for display by stripping the boilerplate suffix that
 * dental sites tack on: the business name, the city, and the state abbreviation
 * (plus the separators around them). Turns
 *   "Cold Sore Treatment Alpharetta, GA — Revive Dental"
 * into
 *   "Cold Sore Treatment".
 * Falls back to the original title if stripping would leave it empty.
 */
function cleanTitle(
  title: string | null,
  businessName: string | null,
  businessAddress: string | null
): string {
  if (!title) return "";
  let t = title;

  // Derive city + state from the address ("123 Main St, Alpharetta, GA 30009").
  const { city, state } = parseCityState(businessAddress);

  // Build a list of substrings to remove (longest first so we don't leave
  // fragments). Case-insensitive, whole-token where it matters.
  const targets: string[] = [];
  if (businessName) targets.push(businessName.trim());
  if (city && state) targets.push(`${city}, ${state}`);
  if (city) targets.push(city);
  if (state) targets.push(state);

  for (const target of targets) {
    if (!target) continue;
    const escaped = target.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    t = t.replace(new RegExp(escaped, "gi"), " ");
  }

  // Collapse leftover separators (—, -, |, ,, ·) and whitespace, then trim
  // any dangling separators/commas from the ends.
  t = t
    .replace(/\s+/g, " ")
    .replace(/\s*[—–\-|·,]\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return t.length > 0 ? t : title;
}

/** Parse city + 2-letter state from a freeform US address. Best-effort. */
function parseCityState(address: string | null): {
  city: string | null;
  state: string | null;
} {
  if (!address) return { city: null, state: null };
  // Look for "..., City, ST ..." — the state is a 2-letter token, the city is
  // the comma-separated chunk right before it.
  const m = address.match(/,\s*([A-Za-z .'-]+),\s*([A-Z]{2})\b/);
  if (m) return { city: m[1]!.trim(), state: m[2]!.trim() };
  // Fallback: "City, ST" with no leading comma.
  const m2 = address.match(/([A-Za-z .'-]+),\s*([A-Z]{2})\b/);
  if (m2) return { city: m2[1]!.trim(), state: m2[2]!.trim() };
  return { city: null, state: null };
}
